import { Box } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { Link } from "react-router-dom";
import {TestCaseHistory} from "../../../features/testcases/TestCaseHistory";
import TestStepsContainer from "../../../features/testcases/test-steps/TestStepsContainer";
import { useNotification } from "../../../hooks/NotificationContext";
import { getTestCase } from "../../../modules/jira";
import { getAssociatedUsers } from "../../../modules/project";
import ConfirmationModal from "../../elements/ConfirmationModal";
import Error from "../../elements/Error";
import Loader from "../../elements/Loader";
import SecondaryHeader from "../../elements/SecondaryHeader";
import Comments from "../../elements/common/comments/Comments";
import {
  archiveViewPageTabs,
  singleViewPageTabs,
} from "../test-case/testCaseTableHead";
import TestCaseDetails from "./single-test-case/TestCaseDetails";
import TestCaseExecutionTable from "./single-test-case/TestCaseExecutionTable";
import Attachments from "../../../features/attachments/Attachments";
import LinkedIssue from "../../../features/traceability/LinkedIssue";
import WebLinks from "../../../features/traceability/WebLinks";

const entityType = "TestCase";
const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const TestCaseViewPage = () => {
  const query = useQuery();
  const searchTerm = query.get("type");
  const [activeTab, setActiveTab] = useState(searchTerm || "details");
  const { testCaseKey, projectId } = useParams();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [testCaseId, setTestCaseId] = useState(null);
  const [error, setError] = useState(false);

  const [unsavedState, setUnsavedState] = useState({ isEditing: false, isFormEdited: false });
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const saveRef = useRef(null);
  const navigate = useNavigate();

  const hasUnsavedChanges = unsavedState.isEditing && unsavedState.isFormEdited;
  const hasUnsavedChangesRef = useRef(hasUnsavedChanges);
  useEffect(() => { hasUnsavedChangesRef.current = hasUnsavedChanges; }, [hasUnsavedChanges]);

  const modalOpenRef = useRef(false);
  useEffect(() => { modalOpenRef.current = showUnsavedModal; }, [showUnsavedModal]);

  useEffect(() => {
    const handler = (e) => {
      if (!hasUnsavedChangesRef.current) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  useEffect(() => {
    const originalPush = window.history.pushState.bind(window.history);

    window.history.pushState = (state, title, url) => {
      if (!hasUnsavedChangesRef.current || modalOpenRef.current) {
        originalPush(state, title, url);
        return;
      }

      const currentPath = window.location.pathname;
      const targetUrl = url ? String(url) : "";
      const targetPath = targetUrl.startsWith("http")
        ? new URL(targetUrl).pathname
        : targetUrl.split("?")[0];

      if (targetPath === currentPath) {
        originalPush(state, title, url);
        return;
      }

      setPendingAction({ type: "route", path: targetUrl });
      setShowUnsavedModal(true);
    };

    return () => {
      window.history.pushState = originalPush;
    };
  }, []);

  const { addNotification } = useNotification();

  const fetchTestCaseData = async () => {
    setLoading(true);
    try {
      const response = await getTestCase(testCaseKey, projectId);
      setTestCaseId(response?.id);
      setData(response);
    } catch (error) {
      if (error?.message === "NOT_FOUND") {
        setError("Test case with the selected key does not exist.");
      } else {
        addNotification("danger", error?.message || "Unable to fetch test case. Please try again later...");
      }
    } finally {
      setLoading(false);
    }
  };

  const getUsersFromJira = async () => {
    try {
      const usersResponse = await getAssociatedUsers(projectId);
      if (usersResponse) {
        setUsers(usersResponse?.map((user) => user?.user));
      }
    } catch (error) {
      addNotification("Error fetching Jira users:", error);
    }
  };

  useEffect(() => {
    fetchTestCaseData();
    getUsersFromJira();
  }, [testCaseKey]);

  const handleTabChange = (tab) => {
    if (hasUnsavedChanges) {
      setPendingAction({ type: "tab", tab });
      setShowUnsavedModal(true);
      return;
    }
    setActiveTab(tab);
  };

  const executePendingAction = (action) => {
    if (!action) return;
    if (action.type === "tab") {
      setActiveTab(action.tab);
    } else if (action.type === "route") {
      navigate(action.path);
    }
  };

  const handleUnsavedDiscard = () => {
    const action = pendingAction;
    setShowUnsavedModal(false);
    setUnsavedState({ isEditing: false, isFormEdited: false });
    setPendingAction(null);
    executePendingAction(action);
  };

  const handleUnsavedSave = async () => {
    await saveRef.current?.();
    const action = pendingAction;
    setShowUnsavedModal(false);
    setPendingAction(null);
    executePendingAction(action);
  };

  const handleUnsavedModalClose = () => handleUnsavedDiscard();

  if (error) {
    return (
      <Error>
        <h2>Test Case Not Found</h2>
        <p className="text-center">
          The test case you're looking for doesn't exist or may have been removed.{" "}
          <Link to={`/project/${projectId}/testcases`}>Click here{" "}</Link>to view
          all test cases
        </p>
      </Error>
    );
  }

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <Box>
          <SecondaryHeader
            activeTab={activeTab}
            setActiveTab={handleTabChange}
            pageTitle={data?.summary}
            breadcrumbs={[
              { label: "Test Cases", path: `/project/${projectId}/testcases`, clickable: true },
              { label: data?.key, path: `/project/${projectId}/testcases/${testCaseKey}`, clickable: false },
            ]}
            tabHeaders={data?.is_archived ? archiveViewPageTabs : singleViewPageTabs}
            showBackButton={true}
          />

          <Box>
            {activeTab === "details" && (
              <TestCaseDetails
                data={data}
                testCaseId={testCaseId}
                fetchTestCaseData={fetchTestCaseData}
                projectId={projectId}
                onUnsavedStateChange={(isEditing, isFormEdited) =>
                  setUnsavedState({ isEditing, isFormEdited })
                }
                saveRef={saveRef}
              />
            )}
            {activeTab === "testscripts" && (
              <TestStepsContainer data={data} testCaseId={testCaseId} />
            )}
            {activeTab === "execution" && (
              <TestCaseExecutionTable
                testCaseId={testCaseId}
                users={users}
                testCaseKey={data?.key}
              />
            )}
            {activeTab === "traceability" && (
              <>
                <LinkedIssue entityId={testCaseId}
                  entityType={entityType}
                  entityKey={data?.key || testCaseKey} />
                <WebLinks entityId={testCaseId}
                  entityType={entityType}
                  entityKey={data?.key || testCaseKey} />
              </>
            )}
            {activeTab === "attachments" && (
              <Box>
                <Attachments users={users || []} entityType={entityType} entityId={testCaseId} />
              </Box>
            )}
            {activeTab === "comments" && (
              <Box>
                <Comments
                  entityType={entityType}
                  entityId={testCaseId}
                  users={users || []}
                  entityKey={data?.key}
                />
              </Box>
            )}
            {activeTab === "history" && (
              <Box>
                <TestCaseHistory
                  entityType={entityType}
                  entityId={testCaseId}
                  users={users || []}
                  projectId={projectId}
                />
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* Unsaved changes confirmation */}
      <ConfirmationModal
        show={showUnsavedModal}
        handleClose={handleUnsavedModalClose}
        handleConfirm={handleUnsavedSave}
        handleSecondary={handleUnsavedDiscard}
        title="Unsaved Changes"
        body="You have unsaved changes. Would you like to save them before leaving?"
        buttonLabel="Save & Continue"
        buttonLabelSecondary="Discard"
        buttonStyle="primary"
      />
    </>
  );
};

export default TestCaseViewPage;
